import { useState } from 'react';
import {
  Alert, Card, CardContent, IconButton, Snackbar, Stack, Tooltip, Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import type { DatosBancarios } from '../types/datosBancarios';

interface Props {
  datos: DatosBancarios;
  /** Código del cupón: si se pasa, se sugiere informarlo en el concepto. */
  codigoCupon?: string;
}

/**
 * Datos bancarios de ATSA para pagar por transferencia (cuenta única BNA,
 * ADR #042). Reutilizable en el dashboard de la empresa y en el detalle del
 * cupón. Incluye botones de copiar por campo.
 */
export default function DatosBancariosCard({ datos, codigoCupon }: Props) {
  const [aviso, setAviso] = useState<string | null>(null);

  const copiar = (texto: string, label: string) => {
    void navigator.clipboard.writeText(texto).then(() => setAviso(`${label} copiado`));
  };

  const filas: Array<{ label: string; valor: string; copiable?: boolean; mono?: boolean }> = [
    { label: 'Banco', valor: datos.banco },
    { label: 'Titular', valor: datos.razonSocial },
    { label: 'CUIT', valor: datos.cuit, copiable: true, mono: true },
    { label: 'Cuenta', valor: datos.cuenta, copiable: true, mono: true },
    { label: 'CBU', valor: datos.cbu, copiable: true, mono: true },
    { label: 'Alias', valor: datos.alias, copiable: true, mono: true },
  ];

  return (
    <Card sx={{ mb: 3, borderLeft: 4, borderLeftColor: 'primary.main' }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
          <AccountBalanceIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Datos para transferir
          </Typography>
        </Stack>

        <Stack spacing={1.25}>
          {filas
            .filter((f) => f.valor)
            .map((f) => (
              <Stack key={f.label} direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
                  {f.label}:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  fontFamily={f.mono ? 'monospace' : undefined}
                >
                  {f.valor}
                </Typography>
                {f.copiable && (
                  <Tooltip title={`Copiar ${f.label.toLowerCase()}`}>
                    <IconButton
                      size="small"
                      aria-label={`Copiar ${f.label.toLowerCase()}`}
                      onClick={() => copiar(f.valor, f.label)}
                    >
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            ))}
        </Stack>

        <Alert severity="info" sx={{ mt: 2, fontSize: 13 }}>
          {codigoCupon ? (
            <>
              Al transferir, indicá en el concepto o referencia el código del
              cupón <strong>{codigoCupon}</strong> (o el CUIT de tu empresa) para
              que podamos identificar y conciliar tu pago. Una vez transferido,{' '}
              <strong>informá el pago y subí el comprobante</strong> con el botón{' '}
              <em>Informar pago / Subir comprobante</em> de abajo: es obligatorio para
              que ATSA pueda confirmar tu pago.
            </>
          ) : (
            <>
              Al transferir, indicá en el concepto o referencia el código del
              cupón (o el CUIT de tu empresa) para que podamos identificar y
              conciliar tu pago. Una vez transferido, abrí el cupón y{' '}
              <strong>subí el comprobante</strong> con <em>Informar pago / Subir
              comprobante</em>: es obligatorio para que ATSA pueda confirmar tu pago.
            </>
          )}
        </Alert>

        <Snackbar
          open={aviso !== null}
          autoHideDuration={2000}
          onClose={() => setAviso(null)}
          message={aviso}
        />
      </CardContent>
    </Card>
  );
}
